import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store, select } from "@ngrx/store";
import { EMPTY, Observable, combineLatest, of } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, switchMap } from "rxjs/operators";
import { CHServiceActions, DataActions, DevicesActions } from "../actions";

import { Injectable } from "@angular/core";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { SecurityDevicesService } from "@app/device/services/security/security-devices.service";
import { CHZWaveDevice, CHZWaveDeviceResponse, CHZWaveDeviceRoutingInfo } from "@app/project/model/ch-zwave-device";
import { CHDeviceService } from "@app/project/services/ch-device.service";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { DataErrorAction, checkCurrentID } from "@app/shared/resources/data-actions";
import { ZwaveService } from "@app/shared/services/zwave-api";
import { ProjectSelectors } from "..";
import { DataServiceError } from "../../../shared/services/data-service-error";
import { DeleteAllDevices, DeleteDevice, DeviceAction, UpdateDeviceClassObjectFailure } from "../actions/devices.actions";
import { DeviceClassStateObject } from "../clarehome.state";

@Injectable()
export class DeviceEffects {
    constructor(
        private readonly deleteConfirmationService: DeleteConfirmationService,
        private readonly actions$: Actions,
        private readonly deviceService: CHDeviceService,
        private readonly store: Store,
        private readonly securityDeviceService: SecurityDevicesService,
        private readonly zwaveService: ZwaveService,
        private auditLogService: AuditLogService
    ) {}
    private csTestSignalDCO = this.securityDeviceService.INTRUSION_CS_TEST_SIGNAL;
    getDevices$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.GetDevices),
            map((action: DevicesActions.GetDevices) => action.payload),
            switchMap(payload =>
                DataActions.toAction()(
                    this.deviceService.getAll(payload.uuid).pipe(
                        checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), payload.uuid),
                        map(devices => ({ uuid: payload.uuid, result: devices })),
                        catchError(DataServiceError.handleError())
                    ),
                    DevicesActions.GetDevicesSuccess,
                    DevicesActions.GetDevicesFailure
                )
            )
        )
    );

    updateDeviceName$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.UpdateDeviceName),
            map((action: DevicesActions.UpdateDeviceName) => action.payload),
            exhaustMap(payload =>
                DataActions.toAction()(
                    this.deviceService.updateDeviceName(payload.uuid, payload.deviceUid, payload.deviceName).pipe(
                        map(() => ({ uuid: payload.uuid, deviceUid: payload.deviceUid, deviceName: payload.deviceName })),
                        checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), payload.uuid)
                    ),
                    DevicesActions.UpdateDeviceNameSuccess,
                    DevicesActions.UpdateDeviceNameFailure
                )
            ),
            catchError(DataActions.toError(DevicesActions.UpdateDeviceNameFailure))
        )
    );

    updateDeviceNameSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.UpdateDeviceNameSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.saved, null, "device name"))
        )
    );

    deleteDevice$: Observable<Action | Action[]> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.DeleteDevice),
            exhaustMap((action: DeleteDevice) => {
                const removePendingServiceMap = new CHServiceActions.RemovePendingServiceMapDeletion(action.payload);
                return DataActions.toAction(removePendingServiceMap)(
                    this.deviceService
                        .deleteDevice(action.payload.uuid, action.payload.data, action.payload.logAction)
                        .pipe(checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), action.payload.uuid)),
                    DevicesActions.DeleteDeviceSuccess,
                    DevicesActions.DeleteDeviceFailure,
                    action.payload.callbackEmitter
                );
            }),
            catchError(e => of(new CHServiceActions.RemovePendingServiceMapDeletion(e)))
        )
    );

    deleteDeviceSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.DeleteDeviceSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.deleted, null, "device"))
        )
    );

    deleteDeviceFailure$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.DeleteDeviceFailure),
            map(DataActions.handleErrorMessage(ActionWord.deleted, "device"))
        )
    );

    deleteDeviceConfirmation$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.DeleteDeviceConfirmation),
            exhaustMap((action: DeviceAction) =>
                this.deleteConfirmationService.open(
                    action.payload,
                    DevicesActions.DeleteDeviceConfirmationDismiss,
                    DevicesActions.DeleteDevice,
                    action.payload.deviceName ? action.payload.deviceName : action.payload.data.Name
                )
            )
        )
    );

    deleteDeviceConfirmationDismiss$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.DeleteDeviceConfirmationDismiss),
            map((action: DeviceAction) => new CHServiceActions.RemovePendingServiceMapDeletion(action.payload))
        )
    );

    deleteAllDevices$: Observable<Action | Action[]> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.DeleteAllDevices),
            exhaustMap((action: DeleteAllDevices) =>
                DataActions.toAction()(
                    this.deviceService
                        .deleteAllDevicesForDeviceType(
                            action.payload.uuid,
                            action.payload.driverName,
                            action.payload.adapterName,
                            action.payload.addMethod,
                            action.payload.logAction
                        )
                        .pipe(checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), action.payload.uuid)),
                    DevicesActions.DeleteAllDevicesSuccess,
                    DevicesActions.DeleteAllDevicesFailure,
                    action.payload.callbackEmitter
                )
            ),
            catchError(e => of(new CHServiceActions.RemovePendingServiceMapDeletion(e)))
        )
    );

    deleteSensor$: Observable<Action | Action[]> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.DeleteSensor),
            exhaustMap((action: DevicesActions.DeleteSensor) =>
                DataActions.toAction()(
                    this.deviceService
                        .deleteSensor(action.payload.uuid, action.payload.securityZone)
                        .pipe(checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), action.payload.uuid)),
                    DevicesActions.DeleteSensorSuccess,
                    DevicesActions.DeleteSensorFailure,
                    action.payload.callbackEmitter
                )
            ),
            catchError(e => of(new CHServiceActions.RemovePendingServiceMapDeletion(e)))
        )
    );

    deleteSensorSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.DeleteSensorSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.deleted, null, "sensor"))
        )
    );

    deleteSensorConfirmation$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.DeleteSensorConfirmation),
            exhaustMap((action: DevicesActions.DeleteSensor) =>
                this.deleteConfirmationService.open(
                    action.payload,
                    DevicesActions.DeleteSensorConfirmationDismiss,
                    DevicesActions.DeleteSensor,
                    action.payload.securityZone.name
                )
            )
        )
    );

    getDeviceClassObjects$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.GetDeviceClassObjects),
            map((action: DevicesActions.GetDeviceClassObjects) => action.payload),
            mergeMap(payload => {
                const ignoreIDCheck = payload.ignoreIDCheck;
                return this.deviceService.getDeviceClassObjects(payload.uuid, payload.deviceUid, payload.dco).pipe(
                    checkCurrentID<any>(ignoreIDCheck ? null : this.store.pipe(select(ProjectSelectors.getUuid)), payload.uuid),
                    map(dco => {
                        const dcoObject = this.deviceService.objectifyDCOs(dco);
                        const payload1 = {
                            uuid: payload.uuid,
                            deviceUid: payload.deviceUid,
                            deviceClassObjects: dcoObject,
                            deviceClass: dco.DeviceClass,
                        };
                        return new DevicesActions.GetDeviceClassObjectsSuccess(payload1);
                    }),
                    catchError(error => {
                        const dataError = new DataServiceError(error, payload);
                        return of(new DevicesActions.GetDeviceClassObjectsFailure(dataError));
                    })
                );
            })
        )
    );

    getDeviceClassobjectFailure$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.GetDeviceClassObjectsFailure),
            map((d: any) => DataActions.handleErrorMessage(ActionWord.retrieved, "property")(d?.error || d))
        )
    );

    getDeviceClassObjectsForDevices$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.GetDeviceClassObjectsForDevices),
            map((action: DevicesActions.GetDeviceClassObjectsForDevices) => action.payload),
            mergeMap(({ uuid, deviceUids, dco, includeMetadata = false }) =>
                this.deviceService.getDeviceClassObjectsForDevices(uuid, deviceUids, dco, includeMetadata).pipe(
                    checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), uuid),
                    map(
                        (res: { uuid: string; deviceClassObjects: DeviceClassStateObject[] }) =>
                            new DevicesActions.GetDeviceClassObjectsForDevicesSuccess(res)
                    ),
                    catchError(error => of(new DevicesActions.GetDeviceClassObjectsForDevicesFailure({ error }, dco)))
                )
            ),
            catchError(error => of(new DevicesActions.GetDeviceClassObjectsForDevicesFailure({ error })))
        )
    );

    updateDeviceClassObjectProperty$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.UpdateDeviceClassObject),
            map((action: DevicesActions.UpdateDeviceClassObject) => action.payload),
            exhaustMap(payload => {
                const ignoreIDCheck = payload.ignoreIDCheck;
                return this.deviceService
                    .updateDeviceClassObjectProperty(payload.uuid, payload.deviceUid, payload.deviceClass, payload.property, payload.args)
                    .pipe(
                        checkCurrentID<any>(ignoreIDCheck ? null : this.store.pipe(select(ProjectSelectors.getUuid)), payload.uuid),
                        map(() => this.dispatchUpdateDeviceClassObjectSuccess(payload)),
                        catchError(e => {
                            const err = e.error?.message || e.message || e;
                            payload.callbackEmitter.emit(
                                new DevicesActions.UpdateDeviceClassObjectFailure({ error: err }, payload.hideErrorToast)
                            );
                            return of(new DevicesActions.UpdateDeviceClassObjectFailure({ error: err }, payload.hideErrorToast));
                        })
                    );
            }),
            catchError(error => of(new DevicesActions.UpdateDeviceClassObjectFailure({ error })))
        )
    );

    private prepInputs(inputs: any[]) {
        return inputs.reduce((obj, input, currentIndex) => {
            obj[`IN${currentIndex + 1}`] = input;
            return obj;
        }, {});
    }

    invokeDeviceClassObjectOperation$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.InvokeDeviceClassObjectOperation),
            map((action: DevicesActions.InvokeDeviceClassObjectOperation) => action.payload),
            switchMap(payload =>
                this.deviceService
                    .invokeDeviceClassObjectOperation(
                        payload.uuid,
                        payload.deviceUid,
                        payload.deviceClass,
                        payload.operation,
                        this.prepInputs(payload.args)
                    )
                    .pipe(
                        map(result => {
                            payload.callbackEmitter?.emit(result);
                            if (payload.operation === this.csTestSignalDCO.OPERATIONS.SEND_TEST_SIGNAL) {
                                return new DevicesActions.SendSignalTestMessageSuccess();
                            }
                            this.saveLogAction(payload.logAction);
                            return new DevicesActions.InvokeDeviceClassObjectOperationSuccess();
                        }),
                        catchError(error => {
                            const finalizedError = error?.error || error;
                            return of(new DevicesActions.UpdateDeviceClassObjectFailure({ error: finalizedError }));
                        })
                    )
            ),
            catchError(error => of(new DevicesActions.UpdateDeviceClassObjectFailure({ error })))
        )
    );

    invokeDeviceClassObjectOperationSilent$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.InvokeDeviceClassObjectOperationSilent),
            map((action: DevicesActions.InvokeDeviceClassObjectOperationSilent) => action.payload),
            switchMap(payload =>
                this.deviceService
                    .invokeDeviceClassObjectOperation(
                        payload.uuid,
                        payload.deviceUid,
                        payload.deviceClass,
                        payload.operation,
                        this.prepInputs(payload.args)
                    )
                    .pipe(
                        map(result => {
                            payload.callbackEmitter?.emit(result);
                            return new DevicesActions.InvokeDeviceClassObjectOperationSilentSuccess();
                        }),
                        catchError(error => {
                            const finalizedError = error?.error || error;
                            return of(new DevicesActions.UpdateDeviceClassObjectFailure({ error: finalizedError }));
                        })
                    )
            ),
            catchError(error => of(new DevicesActions.UpdateDeviceClassObjectFailure({ error })))
        )
    );

    updateSecureDeviceClassObjectOperation$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.UpdateSecureDeviceClassObjectOperation),
            map((action: DevicesActions.UpdateSecureDeviceClassObjectOperation) => action.payload),
            switchMap(payload =>
                this.deviceService
                    .updateSecureDeviceClassObjectOperation(
                        payload.uuid,
                        payload.deviceUid,
                        payload.deviceClass,
                        payload.property,
                        this.prepInputs(payload.args)
                    )
                    .pipe(
                        map(resp => {
                            payload.callbackEmitter?.emit(resp);
                            if (resp) {
                                return new DevicesActions.InvokeDeviceClassObjectOperationSuccess();
                            }
                            return new DevicesActions.UpdateDeviceClassObjectFailure({ error: "Failed to reset Master PIN" });
                        }),
                        catchError(error => of(new DevicesActions.UpdateDeviceClassObjectFailure({ error })))
                    )
            ),
            catchError(error => of(new DevicesActions.UpdateDeviceClassObjectFailure({ error })))
        )
    );

    updateSecureDeviceClassObjectOperationSilent$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.UpdateSecureDeviceClassObjectOperationSilent),
            map((action: DevicesActions.UpdateSecureDeviceClassObjectOperationSilent) => action.payload),
            switchMap(payload =>
                this.deviceService
                    .updateSecureDeviceClassObjectOperation(
                        payload.uuid,
                        payload.deviceUid,
                        payload.deviceClass,
                        payload.property,
                        this.prepInputs(payload.args)
                    )
                    .pipe(
                        map(resp => {
                            payload.callbackEmitter?.emit(resp);
                            if (resp) {
                                return new DevicesActions.InvokeDeviceClassObjectOperationSilentSuccess();
                            }
                            return new DevicesActions.UpdateDeviceClassObjectFailure({ error: "Failed to reset PIN" });
                        }),
                        catchError(error => of(new DevicesActions.UpdateDeviceClassObjectFailure({ error })))
                    )
            ),
            catchError(error => of(new DevicesActions.UpdateDeviceClassObjectFailure({ error })))
        )
    );

    updateDeviceClassObjectOperationSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.InvokeDeviceClassObjectOperationSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.saved, null, "data"))
        )
    );

    sendSignalTestMessageSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.SendSignalTestMessageSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.sent, null, "message"))
        )
    );

    dispatchUpdateDeviceClassObjectSuccess(payload) {
        const payload1 = {
            uuid: payload.uuid,
            deviceUid: payload.deviceUid,
            deviceClass: payload.deviceClass,
            propertyKey: payload.property,
            propertyValue: payload.args,
        };

        if (payload.logAction) {
            this.saveLogAction({
                ...payload.logAction,
                optionalParams: {
                    ...payload.logAction.optionalParams,
                    uuid: payload.uuid,
                },
            });
        }

        const action = new DevicesActions.UpdateDeviceClassObjectSuccess(payload1);
        if (payload.callbackEmitter) {
            payload.callbackEmitter.emit(action);
        }
        return action;
    }

    updateDeviceClassObjectFailure$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.UpdateDeviceClassObjectFailure),
            switchMap((action: UpdateDeviceClassObjectFailure) => {
                if (action.hideToast) {
                    return EMPTY;
                }
                return of(DataActions.handleErrorMessage(ActionWord.saved, "property")(action as DataErrorAction<any>));
            })
        )
    );

    updateDeviceClassObjectSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.UpdateDeviceClassObjectSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.saved, null, "property"))
        )
    );

    getZWaveDevices$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.ActionTypes.GetZWaveDevices),
            map((action: DevicesActions.GetZWaveDevices) => action.payload),
            switchMap(payload =>
                DataActions.toAction()(
                    combineLatest([this.zwaveService.getZWaveDevices(payload.uuid), this.zwaveService.getZWaveHops(payload.uuid)]).pipe(
                        map(([deviceResponse, hops]: [CHZWaveDeviceResponse, CHZWaveDeviceRoutingInfo[]]) => {
                            const returnData: { [key: string]: CHZWaveDevice } = deviceResponse.config
                                .filter(d => !d["parent.UID"])
                                .reduce((acc, device) => {
                                    acc[device.UID] = this.zwaveService.getUIZWaveDeviceFromConfigItem(device, hops);
                                    return acc as { [key: string]: CHZWaveDevice };
                                }, {});
                            return { uuid: payload.uuid, result: returnData };
                        })
                    ),
                    DevicesActions.GetZWaveDevicesSuccess,
                    DevicesActions.GetZWaveDevicesFailure
                )
            )
        )
    );

    private saveLogAction(logAction: AuditLogInput) {
        if (logAction) {
            const { auditName, ...log } = logAction;
            this.auditLogService.logEvent(logAction.auditName, log);
        }
    }
}
